import { useEffect } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import i18n from '@i18n/config';
import { trackException } from '@utils/telemetry';

// Helper to get translation with fallback (ErrorFallback renders outside React context)
const t = (key: string, fallback: string): string => {
  try {
    const fullKey = `common:${key}`;

    const translated: unknown = (i18n.t as (k: string) => unknown)(fullKey);
    return typeof translated === 'string' && translated !== fullKey ? translated : fallback;
  } catch {
    return fallback;
  }
};

// Hardcoded colors from theme (ErrorFallback renders outside ThemeProvider)
// These must be kept in sync with the MUI theme defined in Frontend/mcm.client/src/themes/
const colors = {
  primaryDark: '#0A4A4C',
  primaryMain: '#0F6466',
  secondaryMain: '#3681c3',
  errorMain: '#DC2626',
  errorDark: '#B91C1C',
  textSecondary: '#475569',
  backgroundPaper: '#FFFFFF',
};

function ErrorFallback({ error }: FallbackProps) {
  // Track the error with Application Insights
  useEffect(() => {
    if (error instanceof Error) {
      trackException(error, { source: 'ErrorBoundary', fatal: 'true' });
    }
  }, [error]);
  const handleRefresh = () => {
    globalThis.location.reload();
  };

  // Reset body margin/padding when this component mounts
  useEffect(() => {
    document.body.style.margin = '0';
    document.body.style.padding = '0';
  }, []);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${colors.primaryDark} 0%, ${colors.primaryMain} 50%, ${colors.secondaryMain} 100%)`,
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: 440,
          width: '90%',
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0px 10px 25px rgba(0, 0, 0, 0.3)',
          backgroundColor: colors.backgroundPaper,
        }}
      >
        <div
          style={{
            background: `linear-gradient(135deg, ${colors.primaryMain} 0%, ${colors.primaryDark} 100%)`,
            padding: '24px',
            textAlign: 'center',
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: 700,
              color: 'white',
            }}
          >
            Ma Carte de Membre
          </h1>
        </div>
        <div
          style={{
            padding: '24px 32px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginBottom: 16,
            }}
          >
            <ErrorOutlineIcon style={{ fontSize: '1.8rem', color: colors.errorMain }} />
            <span
              style={{
                fontSize: '1.8rem',
                fontWeight: 600,
                color: colors.errorMain,
              }}
            >
              {t('errorFallback.title', 'Une erreur est survenue')}
            </span>
          </div>
          <p
            style={{
              margin: '0 0 24px 0',
              fontSize: '1rem',
              lineHeight: 1.6,
              color: colors.textSecondary,
            }}
          >
            {t('errorFallback.message', "Nous sommes désolés, une erreur inattendue s'est produite. Veuillez rafraîchir la page ou contacter l'équipe de soutien de votre syndicat si le problème persiste.")}
          </p>

          <button
            type="button"
            onClick={handleRefresh}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 24px',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'white',
              backgroundColor: colors.primaryMain,
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryDark;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryMain;
            }}
            onFocus={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryDark;
            }}
            onBlur={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryMain;
            }}
          >
            <RefreshIcon style={{ fontSize: 20 }} />
            {t('errorFallback.refreshButton', 'Rafraîchir la page')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ErrorFallback;
